import React, { useState, useEffect } from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { Youtube, Play, ExternalLink, Users, BookOpen, Video } from 'lucide-react';

const YouTubePage = () => {
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch videos from backend API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/youtube/videos`);
        const data = await response.json();
        
        if (data.success) {
          setVideos(data.data.videos || []);
        } else {
          setError('Failed to load videos');
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to load videos');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Filter videos based on selected language
  const filteredVideos = videos.filter(video => {
    // Language filter - map filter codes to full language names in database
    let languageMatch = false;
    
    if (selectedLanguage === 'all') {
      languageMatch = true;
    } else if (selectedLanguage === 'EN') {
      languageMatch = !video.language || video.language === 'EN' || video.language === 'English';
    } else if (selectedLanguage === 'FR') {
      languageMatch = video.language === 'FR' || video.language === 'French';
    } else if (selectedLanguage === 'PT') {
      languageMatch = video.language === 'PT' || video.language === 'Portuguese';
    } else if (selectedLanguage === 'AR') {
      languageMatch = video.language === 'AR' || video.language === 'Arabic';
    }
    
    // Only show published videos
    const isPublished = video.is_published === 1 || video.is_published === true;
    
    return languageMatch && isPublished;
  });

  const channelUrl = 'https://www.youtube.com/channel/UCD8XFIS4GhpslR3_Xqnj-xg';

  if (loading) {
    return (
      <PublicLayout>
        <div className="container py-5 text-center">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading videos...</p>
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="container py-5 text-center">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO 
        title="ARCA YouTube Channel - Educational Videos & Webinars"
        description="Watch ARCA's educational videos, webinars, training sessions, and respiratory care content. Available in multiple languages including English, French, Portuguese, and Arabic."
        keywords="ARCA videos, respiratory care videos, medical webinars, healthcare training videos, YouTube channel"
        url="/media/youtube-videos"
      />
      
      {/* Hero Section - Reduced size */}
      <section className="position-relative text-white py-4" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <Youtube className="mb-2" size={30} />
              <h1 className="fs-3 fw-bold mb-2">ARCA YouTube Channel</h1>
              <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                Watch our educational videos, webinars, and training content on respiratory care across Africa
              </p>
              <a 
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-light btn-sm fw-bold shadow"
                style={{ color: '#6B8E23' }}
              >
                <Youtube size={16} className="me-1" />
                Visit Our YouTube Channel
                <ExternalLink size={12} className="ms-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="py-4 bg-light">
        <div className="container">
          {/* Section Header */}
          <div className="row mb-4">
            <div className="col-12 text-center">
              <h2 className="fw-bold mb-3" style={{ color: '#6B8E23', fontSize: '1.5rem' }}>
                <Video className="me-1 mb-1" size={24} style={{ color: '#6B8E23' }} />
                ARCA Video Library
              </h2>
              <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                Explore our collection of {filteredVideos.length} educational videos in multiple languages
              </p>
              {/* Language Filter Buttons */}
              <div className="d-flex justify-content-center gap-2 flex-wrap mb-4">
                <button 
                  className={`badge border px-3 py-2 ${selectedLanguage === 'all' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLanguage('all')}
                >
                  <i className="bi bi-globe me-1"></i> All Languages
                </button>
                <button 
                  className={`badge border px-3 py-2 ${selectedLanguage === 'EN' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLanguage('EN')}
                >
                  <i className="bi bi-globe me-1"></i> English
                </button>
                <button 
                  className={`badge border px-3 py-2 ${selectedLanguage === 'FR' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLanguage('FR')}
                >
                  <i className="bi bi-globe me-1"></i> French
                </button>
                <button 
                  className={`badge border px-3 py-2 ${selectedLanguage === 'PT' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLanguage('PT')}
                >
                  <i className="bi bi-globe me-1"></i> Portuguese
                </button>
                <button 
                  className={`badge border px-3 py-2 ${selectedLanguage === 'AR' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLanguage('AR')}
                >
                  <i className="bi bi-globe me-1"></i> Arabic
                </button>
              </div>
            </div>
          </div>

          {/* Videos Grid - 4 columns */}
          <div className="row g-4">
            {filteredVideos.map((video, index) => (
              <div key={video.id || index} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div 
                  className="card h-100 border-0 shadow-sm overflow-hidden"
                  style={{ 
                    transition: 'all 0.3s ease',
                    transform: hoveredVideo === index ? 'translateY(-8px)' : 'translateY(0)',
                    boxShadow: hoveredVideo === index ? '0 8px 16px rgba(107, 142, 35, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={() => setHoveredVideo(index)}
                  onMouseLeave={() => setHoveredVideo(null)}
                >
                  <div className="position-relative" style={{ cursor: 'pointer' }} onClick={() => window.open(video.video_url, '_blank')}>
                    <img 
                      src={video.thumbnail_url || `https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
                      alt={video.title}
                      className="card-img-top"
                      style={{ height: '180px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`;
                      }}
                    />
                    {/* Language Badge */}
                    {video.language && (
                      <span 
                        className="position-absolute top-0 end-0 m-2 badge" 
                        style={{ backgroundColor: '#6B8E23', fontSize: '0.7rem' }}
                      >
                        {video.language}
                      </span>
                    )}

                    {/* Play Button Overlay */}
                    <div 
                      className="position-absolute top-50 start-50 translate-middle"
                      style={{ 
                        opacity: hoveredVideo === index ? 1 : 0.8,
                        transition: 'opacity 0.3s ease'
                      }}
                    >
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          backgroundColor: '#6B8E23',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                        }}
                      >
                        <Play className="text-white" size={24} fill="white" />
                      </div>
                    </div>
                    {/* Video Number */}
                    <span 
                      className="position-absolute bottom-0 start-0 m-2 badge bg-dark"
                      style={{ fontSize: '0.7rem' }}
                    >
                      #{index + 1}
                    </span>
                  </div>
                  <div className="card-body p-3">
                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.9rem' }}>
                      {video.title}
                    </h6>
                    <a 
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm w-100"
                      style={{ 
                        backgroundColor: hoveredVideo === index ? '#6B8E23' : 'transparent',
                        borderColor: '#6B8E23',
                        color: hoveredVideo === index ? 'white' : '#6B8E23',
                        transition: 'all 0.3s ease',
                        fontSize: '0.8rem'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Youtube size={16} className="me-1" />
                      Watch Video
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredVideos.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No videos found matching your selected filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-4" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="text-center text-white">
                <Youtube size={30} className="mb-3" />
                <h2 className="fw-bold mb-2" style={{ fontSize: '1.5rem' }}>Subscribe to Our Channel</h2>
                <p className="mb-3" style={{ maxWidth: '700px', margin: '0 auto', fontSize: '0.9rem' }}>
                  Stay updated with our latest videos, webinars, training sessions, and respiratory care educational content from across Africa
                </p>
                <a 
                  href={channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light btn-sm fw-bold shadow"
                  style={{ color: '#6B8E23' }}
                >
                  <Youtube size={16} className="me-1" />
                  Subscribe Now
                  <ExternalLink size={12} className="ms-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Channel Info */}
      <section className="py-4 bg-white">
        <div className="container">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-3 text-center">
                  <div className="rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" 
                    style={{ width: '60px', height: '60px', backgroundColor: 'rgba(107, 142, 35, 0.1)' }}>
                    <Play style={{ color: '#6B8E23' }} size={30} />
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: '#6B8E23', fontSize: '1rem' }}>Educational Content</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    Access training videos, webinars, and educational sessions on respiratory care
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-3 text-center">
                  <div className="rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" 
                    style={{ width: '60px', height: '60px', backgroundColor: 'rgba(107, 142, 35, 0.1)' }}>
                    <Users style={{ color: '#6B8E23' }} size={30} />
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: '#6B8E23', fontSize: '1rem' }}>Community Events</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    Watch recordings of conferences, workshops, and community events from across Africa
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-3 text-center">
                  <div className="rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" 
                    style={{ width: '60px', height: '60px', backgroundColor: 'rgba(107, 142, 35, 0.1)' }}>
                    <BookOpen style={{ color: '#6B8E23' }} size={30} />
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: '#6B8E23', fontSize: '1rem' }}>Expert Insights</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    Learn from leading respiratory care professionals and healthcare experts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default YouTubePage;